/**
 * API client for the Farmschool Cloudflare Worker.
 *
 * Uses fetch with JWT Bearer token from localStorage.
 * All responses follow the ApiResult<T> shape:
 *   { success: true, data: T } | { success: false, error: string }
 */

import { getToken, handleUnauthorized } from './auth'

const API_URL =
  import.meta.env.VITE_API_URL ?? 'https://school-homestead-api.zach-fischer15.workers.dev'

interface ApiResultOk<T> {
  success: true
  data: T
}
interface ApiResultErr {
  success: false
  error: string
}
export type ApiResponse<T> = ApiResultOk<T> | ApiResultErr

/**
 * Makes an authenticated request to the Worker API.
 * Automatically attaches the JWT Bearer token from localStorage.
 * Throws on network errors; returns ApiResult on HTTP responses.
 */
export async function apiRequest<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = getToken()

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>)
  }

  // Only set Content-Type when a body is present to avoid Hono trying to
  // parse an empty body on DELETE / GET requests.
  if (options.body !== undefined && options.body !== null) {
    headers['Content-Type'] = 'application/json'
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers
  })

  // Session expired or token rejected by the server — clear auth and force
  // re-login. Auth endpoints are excluded so a failed login attempt does not
  // trigger a redirect loop.
  if (res.status === 401 && !path.startsWith('/api/auth')) {
    handleUnauthorized()
  }

  // Guard against non-JSON responses (e.g. HTML error pages from the edge).
  const text = await res.text()
  try {
    const json = JSON.parse(text)
    return json as ApiResponse<T>
  } catch {
    console.error('[api] Non-JSON response:', res.status, text.slice(0, 200))
    return { success: false, error: `Server error (${res.status})` }
  }
}

// Convenience methods
export const api = {
  get: <T>(path: string) => apiRequest<T>(path),

  post: <T>(path: string, body: unknown) =>
    apiRequest<T>(path, {
      method: 'POST',
      body: JSON.stringify(body)
    }),

  put: <T>(path: string, body: unknown) =>
    apiRequest<T>(path, {
      method: 'PUT',
      body: JSON.stringify(body)
    }),

  patch: <T>(path: string, body: unknown) =>
    apiRequest<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body)
    }),

  delete: <T>(path: string) =>
    apiRequest<T>(path, {
      method: 'DELETE'
    })
}
